import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { t } from 'i18next';
import _ from 'lodash';
import {
  AllColumnEnumOptions,
  CanonicalCategoryOrder,
  RuleGroupToRuleOrder,
} from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import { ColumnRulesWithMetaData } from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.constants';
import { getColumnRuleWithMetaData } from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.utilities';
import { PropertyMatchOperatorEnum } from '@seeqdev/api/model/ColumnRuleDescendantInputV1';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';
import type { BaseTableDefinitionStore } from '@/tableDefinitionEditor/baseTableDefinitionStore';
import type { ColumnRule } from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import type { ColumnRuleWithMetadata } from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.types';
import type { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import type { ColumnDefinitionOutputV1 } from '@seeqdev/api/model/ColumnDefinitionOutputV1';
import type { ColumnRuleInputV1 } from '@seeqdev/api/model/ColumnRuleInputV1';
import type { ColumnRuleOutputV1 } from '@seeqdev/api/model/ColumnRuleOutputV1';

export const getAllowedColumnTypes = (columnRule: ColumnRule): ColumnTypeEnum[] => {
  return getColumnRuleWithMetaData(columnRule)?.allowedOnColumnTypes ?? [];
};

const getRulesAllowedOnColumnType = (columnType?: ColumnTypeEnum): ColumnRuleWithMetadata[] => {
  return columnType
    ? ColumnRulesWithMetaData.filter((columnRule) => columnRule.allowedOnColumnTypes.includes(columnType))
    : ColumnRulesWithMetaData;
};

const hierarchyRules: string[] = [
  SeeqNames.MaterializedTables.Rules.Ancestor,
  SeeqNames.MaterializedTables.Rules.Descendant,
  SeeqNames.MaterializedTables.Rules.PathSearch,
  SeeqNames.MaterializedTables.Rules.GetItemProperty,
];

const eventRules: string[] = [
  SeeqNames.MaterializedTables.Rules.DescendantSearch,
  SeeqNames.MaterializedTables.Rules.EventAssetCreator,
  SeeqNames.MaterializedTables.Rules.FindProperty,
  SeeqNames.MaterializedTables.Rules.FindDescendant.BaseFindDescendant,
];

export const getRulesAllowed = (
  isFirstRuleOnColumn: boolean,
  includesHierarchy: boolean,
  columnType?: ColumnTypeEnum,
  columnRuleInputToEdit?: ColumnRuleInputV1,
  subscriberId?: string,
): ColumnRuleWithMetadata[] => {
  const allowedOnType = getRulesAllowedOnColumnType(columnType);
  const rulesFilteredByFallback = allowedOnType.filter((columnRule) => columnRule.canBeFallback !== false);
  const allowedRules = isFirstRuleOnColumn ? allowedOnType : rulesFilteredByFallback;
  const columnRuleIsSetItemProperty = !!columnRuleInputToEdit?.setItemProperty;
  const allowedRulesMinusSetProperty = columnRuleIsSetItemProperty
    ? allowedRules
    : allowedRules.filter(
        (columnRule) =>
          columnRule.rule !== SeeqNames.MaterializedTables.Rules.SetItemProperty &&
          columnRule.rule !== SeeqNames.MaterializedTables.Rules.DescendantSearch,
      );
  let results =
    !subscriberId || includesHierarchy
      ? allowedRulesMinusSetProperty
      : allowedRulesMinusSetProperty.filter(
          (columnRule) => columnRule.rule !== SeeqNames.MaterializedTables.Rules.EventProperty,
        );
  if (includesHierarchy) {
    results = results.filter((columnRule) => !hierarchyRules.includes(columnRule.rule));
  }
  if (!includesHierarchy) {
    results = results.filter((columnRule) => !eventRules.includes(columnRule.rule));
  }
  return results;
};

export const getAllowedColumnTypesGivenCurrentColumnAndSetOfRules = (
  ruleInputs: ColumnRuleInputV1[],
  currentColumnTypeIfConfigured?: ColumnTypeEnum,
): ColumnTypeEnum[] => {
  return ruleInputs.reduce((commonTypes, ruleInput) => {
    const rule: ColumnRule = getRuleTypeFromRuleInput(ruleInput);
    let currentList: ColumnTypeEnum[];
    if (
      rule === SeeqNames.MaterializedTables.Rules.Constant.BaseConstant &&
      currentColumnTypeIfConfigured !== undefined
    ) {
      // A constant rule that has been configured on a given column is by necessity of the same type as the
      // column, so we don't want to allow changing the column type if you have a configured constant rule
      currentList = [currentColumnTypeIfConfigured];
    } else {
      currentList = getAllowedColumnTypes(rule);
    }
    return _.compact(commonTypes.filter((type) => currentList.includes(type)));
  }, AllColumnEnumOptions);
};

export const getRuleTypeFromRuleInput = (ruleInput: ColumnRuleInputV1): ColumnRule => {
  // Each rule input should have only one field filled out, so we can just get the first one as the rule type
  return Object.keys(ruleInput)[0] as ColumnRule;
};

export const columnRuleOutputToColumnRuleInput = (
  ruleOutput: ColumnRuleOutputV1,
  otherColumns: ColumnDefinitionOutputV1[],
): ColumnRuleInputV1 => {
  // The constant rule is unique in that there is only 1 api input type but multiple api output types
  const isConstantRule = /constant/i.test(ruleOutput.rule);
  const isScalarCreatorRule = /scalarCreator/i.test(ruleOutput.rule);
  const isFindDescendantRule = /findDescendant/i.test(ruleOutput.rule);
  let ruleType;
  if (isConstantRule) {
    ruleType = SeeqNames.MaterializedTables.Rules.Constant.BaseConstant;
  } else if (isScalarCreatorRule) {
    ruleType = SeeqNames.MaterializedTables.Rules.ScalarCreator.BaseScalarCreator;
  } else if (isFindDescendantRule) {
    ruleType = SeeqNames.MaterializedTables.Rules.FindDescendant.BaseFindDescendant;
  } else {
    ruleType = _.lowerFirst(ruleOutput.rule);
  }
  if (!isValidColumnRule(ruleType)) {
    throw new Error(`Invalid rule type: ${ruleType}`);
  }

  const columnRuleWithMetadata = getColumnRuleWithMetaData(ruleType);
  if (!columnRuleWithMetadata) {
    throw new Error(`Could not find metadata for rule: ${ruleType}`);
  }

  return {
    [ruleType]: columnRuleWithMetadata.columnRuleOutputToColumnRuleInput(ruleOutput, otherColumns),
  };
};

const isValidColumnRule = (rule: string): rule is ColumnRule => {
  return ColumnRulesWithMetaData.some((columnRule) => columnRule.rule === rule);
};

const getDefaultColumnNameBase = () => t('SCALING.NEW_COLUMN_NAME_BASE');
export const generateColumnName = (tableDefinitionStore: BaseTableDefinitionStore, baseName?: string): string => {
  const columns = tableDefinitionStore.columns;
  const existingColumnNames = columns
    .map((col) => col.displayName)
    // The following are reserved names and should not be used
    .concat([SeeqNames.MaterializedTables.DatumIdColumn, SeeqNames.MaterializedTables.ItemIdColumn]);

  return incrementGeneratedName(existingColumnNames, baseName);
};

export const incrementGeneratedName = (existingColumnNames: string[], baseName?: string) => {
  const base = baseName ?? getDefaultColumnNameBase();
  const existingNames = new Set(existingColumnNames);
  let newName = base;
  let i = 2;
  while (existingNames.has(newName)) {
    newName = `${base} ${i}`;
    i++;
  }
  return newName;
};

const sortGroup = (itemGroup: string) => {
  return CanonicalCategoryOrder.indexOf(itemGroup);
};

export const groupItems = (ungrouped: readonly ReactSelectOption[]) => {
  const groupItem = (item: ReactSelectOption) => item?.group;

  return _.chain(ungrouped)
    .groupBy(groupItem)
    .sortBy((obj) => sortGroup(obj[0].group!))
    .map((items) => {
      const sortedItems = _.sortBy(items, (item) => {
        const idx = RuleGroupToRuleOrder[item.group!]?.indexOf(item.value as string);
        return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
      });
      return { label: t(items[0].group!), options: sortedItems };
    })
    .value();
};

export const getOperatorOptions = (type: string) => {
  switch (type) {
    case 'STRING':
      return Object.values(PropertyMatchOperatorEnum) as PropertyMatchOperatorEnum[];
    case 'NUMBER':
    case 'BOOLEAN':
      return (Object.values(PropertyMatchOperatorEnum) as PropertyMatchOperatorEnum[]).filter(
        (enumValue) =>
          enumValue === PropertyMatchOperatorEnum.EQUALS || enumValue === PropertyMatchOperatorEnum.NOTEQUALS,
      );
    default:
      return Object.values(PropertyMatchOperatorEnum) as PropertyMatchOperatorEnum[];
  }
};

export const getInputType = (type: string) => {
  switch (type) {
    case 'STRING':
      return 'text';
    case 'NUMBER':
      return 'number';
    case 'BOOLEAN':
      return 'boolean';
    default:
      return 'text';
  }
};

export const getPropertyOptions = (
  searchPropertyOptions: ReactSelectOption[],
  propertyNames: (string | null)[],
  propertyName: string,
) =>
  searchPropertyOptions.filter((option) => {
    const optionValue = option.value!.toString();

    const isSameProperty = optionValue === propertyName;
    const isExcludedByNames = propertyNames.filter((name) => name !== propertyName).includes(optionValue);

    return isSameProperty || !isExcludedByNames;
  });
